import { useLazyGetWorkspacesQuery } from '@carboncopyco/data';
import { notify } from '@carboncopyco/ui';
import { useEffect } from 'react';
import { AppEventsEnum } from '../../shared/enums/AppEventsEnum';
import { useTabManager } from '../services/TabManager';
import { useUser, useWorkspaces } from '../store/context';
import { SidebarContentType } from '../types/WorkspacesTypes';
import { useCustomNavigate } from './useCustomNavigate';
import { useFocusAppService } from './useFocusAppService';
import { useWorkspacesMap } from './useWorkspacesMap';

export const useDeeplinkNavigate = () => {
  const workspacesMap = useWorkspacesMap();
  const [getWorkspaces] = useLazyGetWorkspacesQuery();
  const { navigateToRoute, navigateToWorkspace } = useCustomNavigate();
  const { activeWorkspace, switchWorkspace } = useWorkspaces();
  const { hideOnSwitchFocus } = useFocusAppService();
  const { switchToHomeTab } = useTabManager();

  const { activeOrganization, setActiveOrganization } = useUser();

  const { closeAllGlobalTabs } = useTabManager();

  const navigateToNewOrganization = async (organizationId: string) => {
    closeAllGlobalTabs();
    navigateToRoute(`${organizationId}`);
    const storageOrganizations =
      await window?.dsDesktop?.user.getOrganizations();
    const newOrganization = storageOrganizations.find(
      (org) => org?.id === Number.parseInt(organizationId, 10)
    );
    if (!newOrganization) {
      notify.error('Invalid deeplink');
      return;
    }
    await window.dsDesktop.user.updateSelectedOrganization(newOrganization);
    setActiveOrganization(newOrganization);
  };

  const navigateToNewWorkspace = async (
    workspaceId: string,
    organizationId: string,
    searchParams = ''
  ) => {
    try {
      const workspacesResponse = await getWorkspaces({
        organizationId,
      });
      const workspace =
        workspacesMap[Number.parseInt(workspaceId, 10)] ||
        workspacesResponse?.data?.find(
          (newWorkspace: any) =>
            newWorkspace.id === Number.parseInt(workspaceId, 10)
        );
      if (!workspace) {
        notify.error('Invalid deeplink');
        return;
      }
      switchWorkspace(workspace);
      navigateToWorkspace(workspaceId, searchParams);
    } catch (_error) {
      notify.error('Invalid deeplink');
    }
  };
  const handleNavigateTo = async (...args: any[]) => {
    const { route, params } = args[0];
    const routeOnly = route.split('?')[0];
    const routeParts = routeOnly.split('/');

    const organizationId = routeParts[1] || params.organizationId;
    const workspaceId = routeParts[3] || params.workspaceId;
    const regexTest = /^\/\d+\/workspace\/\d+/;
    if (
      !organizationId ||
      !routeParts?.length ||
      (routeParts[3] && !regexTest.test(routeOnly))
    ) {
      notify.error('Invalid deeplink');
      return;
    }
    hideOnSwitchFocus();

    if (
      organizationId &&
      organizationId !== activeOrganization?.id?.toString()
    ) {
      await navigateToNewOrganization(organizationId);
    } else {
      switchToHomeTab();
    }
    if (!workspaceId) {
      switchWorkspace(null, SidebarContentType.Home);
    } else if (workspaceId !== activeWorkspace.current.id.toString()) {
      await navigateToNewWorkspace(workspaceId, organizationId);
    }
    navigateToRoute(route);
  };

  useEffect(() => {
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.MAIN_TO_APP_NAVIGATE_TO,
      handleNavigateTo
    );
  }, [activeOrganization, workspacesMap]);

  return {
    navigateToNewOrganization,
  };
};
